import { ApolloError, useMutation, useQuery } from "@apollo/client";
import { DeleteOutline } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { type FormEvent, useEffect, useRef, useState } from "react";
import { useNotify, useTranslate } from "react-admin";

import { PlacetelHotlineNumbersDocument, UpdatePlacetelHotlineNumbersDocument } from "../../generated/graphql";
import { captureException } from "../../lib/errorHandling";

type NumberDraft = { key: string; value: string };

// The server's limits: the numbers of the hotline and the length of one entry.
const MAX_NUMBERS = 20;
const MAX_NUMBER_LENGTH = 64;
const HOTLINE_NUMBERS_INVALID_CODE = "placetel-hotline-numbers-invalid";

// Keys identify rows while editing; the entries change, new rows have no stored number yet.
let keyCounter = 0;
const newKey = (): string => {
  keyCounter += 1;
  return `hotline-number-${keyCounter}`;
};

const toDrafts = (numbers: string[]): NumberDraft[] => numbers.map(value => ({ key: newKey(), value }));

// Empty rows are left out; the server normalises the others and refuses an unusable or repeated one.
const toInput = (drafts: NumberDraft[]): string[] =>
  drafts.map(draft => draft.value.trim()).filter(value => value !== "");

export const HotlineNumbersSettings = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const { data, loading, error } = useQuery(PlacetelHotlineNumbersDocument, { fetchPolicy: "network-only" });
  const [drafts, setDrafts] = useState<NumberDraft[]>([]);
  const [initialized, setInitialized] = useState(false);
  const saveInFlight = useRef(false);
  const [updatePlacetelHotlineNumbers, { loading: saving }] = useMutation(UpdatePlacetelHotlineNumbersDocument, {
    update: (cache, { data: mutationData }) => {
      const saved = mutationData?.updatePlacetelHotlineNumbers;
      if (!saved) return;
      cache.writeQuery({ query: PlacetelHotlineNumbersDocument, data: { placetelHotlineNumbers: saved } });
    },
  });

  const numbers = data?.placetelHotlineNumbers;

  useEffect(() => {
    if (loading || error || !numbers || initialized) return;
    setDrafts(toDrafts(numbers));
    setInitialized(true);
  }, [error, initialized, loading, numbers]);

  const changeNumber = (key: string, value: string) =>
    setDrafts(current => current.map(draft => (draft.key === key ? { ...draft, value } : draft)));

  const addNumber = () => setDrafts(current => [...current, { key: newKey(), value: "" }]);

  const removeNumber = (key: string) => setDrafts(current => current.filter(draft => draft.key !== key));

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!initialized || saving || saveInFlight.current) return;

    saveInFlight.current = true;
    try {
      const result = await updatePlacetelHotlineNumbers({ variables: { numbers: toInput(drafts) } });
      const saved = result.data?.updatePlacetelHotlineNumbers;
      if (!saved) throw new Error();

      setDrafts(toDrafts(saved));
      notify("integrations.hotlineNumbers.saved", { type: "success" });
    } catch (saveError) {
      const refused =
        saveError instanceof ApolloError && saveError.graphQLErrors[0]?.message === HOTLINE_NUMBERS_INVALID_CODE;
      if (!refused) captureException(saveError, "placetel/hotline-numbers: save");
      notify(translate(refused ? "integrations.hotlineNumbers.invalid" : "integrations.hotlineNumbers.saveError"), {
        type: "error",
      });
    } finally {
      saveInFlight.current = false;
    }
  };

  if (error) return <Alert severity="error">{translate("integrations.hotlineNumbers.loadError")}</Alert>;
  if (loading || !numbers || !initialized) {
    return <CircularProgress aria-label={translate("integrations.hotlineNumbers.loading")} />;
  }

  return (
    <Card data-testid="hotline-numbers-card">
      <CardContent>
        <Typography component="h2" variant="h6" gutterBottom>
          {translate("integrations.hotlineNumbers.title")}
        </Typography>
        <Typography color="text.secondary" paragraph>
          {translate("integrations.hotlineNumbers.description")}
        </Typography>
        <Box component="form" onSubmit={handleSubmit} noValidate>
          {drafts.length === 0 ? (
            <Typography color="text.secondary" paragraph>
              {translate("integrations.hotlineNumbers.empty")}
            </Typography>
          ) : null}
          {drafts.map(draft => (
            <Box key={draft.key} sx={{ alignItems: "flex-start", display: "flex", gap: 1 }}>
              <TextField
                disabled={saving}
                fullWidth
                label={translate("integrations.hotlineNumbers.number")}
                margin="dense"
                onChange={event => changeNumber(draft.key, event.target.value)}
                size="small"
                slotProps={{ htmlInput: { "data-testid": "hotline-number-input", maxLength: MAX_NUMBER_LENGTH } }}
                value={draft.value}
              />
              <IconButton
                aria-label={translate("integrations.hotlineNumbers.remove")}
                disabled={saving}
                onClick={() => removeNumber(draft.key)}
                sx={{ mt: 0.5 }}
              >
                <DeleteOutline />
              </IconButton>
            </Box>
          ))}
          <Button
            data-testid="hotline-number-add"
            disabled={saving || drafts.length >= MAX_NUMBERS}
            onClick={addNumber}
            type="button"
            variant="outlined"
          >
            {translate("integrations.hotlineNumbers.add")}
          </Button>
          <Box sx={{ mt: 2 }}>
            <Button data-testid="hotline-numbers-save" disabled={saving} type="submit" variant="contained">
              {translate(saving ? "integrations.hotlineNumbers.saving" : "integrations.hotlineNumbers.save")}
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};
